import Category from "components/category";
import ProductDetailPage from "components/ProductDetails/ProductDetailsPage";
import { setCachingHeader } from "helpers/cacheHeader";
import { GetServerSideProps } from "next";
import { FC } from "react";
import { createDatoClient } from "services/datocms";
import {
  DatoProductForCategoryPageFragment,
  getSdk,
  ProductBySlugQuery,
  ProductCategoryFragment,
  ProductPageContentBySlugQuery,
} from "services/datocms/generated";
import { createQueryParamsForCaching } from "utils/create-query-params";

interface ProductOrCategoryPageProps {
  datoProduct?: ProductBySlugQuery["product"] | null;
  productPageContent?: ProductPageContentBySlugQuery["product"];
  products?: DatoProductForCategoryPageFragment[];
  category?: ProductCategoryFragment | null;
}

export const getServerSideProps: GetServerSideProps<
  ProductOrCategoryPageProps
> = async (context) => {
  setCachingHeader(context);

  const slug = context?.params?.slug || "";
  const id = context?.params?.id || "";

  try {
    // Try product slug first, if successful cut out having done one query
    const [{ product }, { machine }] = await Promise.all([
      getSdk(
        createDatoClient(
          context.preview,
          createQueryParamsForCaching({
            queryType: "ProductBySlug",
            queryVariables: { slug: `${id}` },
          }),
        ),
      ).ProductBySlug({ slug: `${id}` }),
      getSdk(
        createDatoClient(
          context.preview,
          createQueryParamsForCaching({
            queryType: "MachineBySlug",
            queryVariables: { slug: `${id}` },
          }),
        ),
      ).MachineBySlug({ slug: `${id}` }),
    ]);

    // Nesting is hard to read here
    // if a product is found, but its category slug doesn't match, 404
    // unless a machine is found, in which case we'll try to render a category filtered by
    // machine
    if (product) {
      if (product.category?.slug != slug) {
        if (!machine) {
          return {
            notFound: true,
          };
        }
      } else {
        return {
          props: {
            datoProduct: product,
          },
        };
      }
    }

    // now try to find a category or label
    const [{ productLabel }, { productCategory }] = await Promise.all([
      getSdk(
        createDatoClient(
          context.preview,
          createQueryParamsForCaching({
            queryType: "LabelBySlug",
            queryVariables: { slug: `${id}` },
          }),
        ),
      ).LabelBySlug({ slug: `${id}` }),
      getSdk(
        createDatoClient(
          context.preview,
          createQueryParamsForCaching({
            queryType: "ProductCategoryBySlug",
            queryVariables: { slug: `${slug}` },
          }),
        ),
      ).ProductCategoryBySlug({
        slug: `${slug}`,
      }),
    ]);
    const allProducts = productCategory?._allReferencingProducts || [];

    if (id != slug && !productLabel && !machine) {
      return {
        notFound: true,
      };
    }

    if (!productCategory && !productLabel) {
      return {
        notFound: true,
      };
    }

    if (productLabel || !!productCategory) {
      // Failsafe for when category is not found
      if (!productCategory && allProducts.length === 0) {
        return {
          notFound: true,
        };
      }

      return {
        props: {
          category: productCategory ?? null,
          products: allProducts,
        },
      };
    }
    return {
      props: {
        datoProduct: product,
      },
    };
  } catch (error) {
    return {
      notFound: true,
    };
  }
};

const ProductOrCategoryPage: FC<ProductOrCategoryPageProps> = ({
  datoProduct,
  category,
  products,
}) => {
  if (category) {
    return <Category category={category} products={products} />;
  }

  if (datoProduct) {
    return <ProductDetailPage datoProduct={datoProduct} />;
  }
};

export default ProductOrCategoryPage;
